import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { produce } from 'immer';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  GetProposalQuery,
  SetProposalValidForDaysInput,
  SetProposalValidForDaysMutation,
} from '@/gql/graphql';

const SET_PROPOSAL_VALID_FOR_DAYS_MUTATION = graphql(`
  mutation SetProposalValidForDays($input: SetProposalValidForDaysInput!) {
    setProposalValidForDays(input: $input) {
      id
      validForDays
    }
  }
`);

/**
 * Set the number of days a Proposal is valid for once sent. Only permitted on
 * draft proposals server-side, so callers should only expose it before send.
 */
const useSetProposalValidForDays = (args?: {
  proposalId?: string;
  options?: UseMutationOptions<
    SetProposalValidForDaysMutation,
    unknown,
    SetProposalValidForDaysInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: SetProposalValidForDaysInput) => {
      return gqlClient.request(SET_PROPOSAL_VALID_FOR_DAYS_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        const queryKey = proposalKeys.proposal(args.proposalId);
        // Patch the returned value straight into the cache so readers update
        // without waiting on the refetch. The invalidation still runs: the
        // sendability gate (actionAvailability.send) is recomputed from the validity
        // server-side, so it must reconcile.
        queryClient.setQueryData<GetProposalQuery>(queryKey, (old) =>
          old
            ? produce(old, (draft) => {
                draft.proposal.validForDays =
                  data.setProposalValidForDays.validForDays;
              })
            : old
        );
        void queryClient.invalidateQueries({ queryKey });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useSetProposalValidForDays;
